import { useState } from "react";
import { createClient } from "https://cdn.jsdelivr.net/npm/@supabase/supabase-js/+esm";

const SUPABASE_URL = "https://rbmleqmfeewmkvmlxblf.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJibWxlcW1mZWV3bWt2bWx4YmxmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njk4ODUyNjEsImV4cCI6MjA4NTQ2MTI2MX0.5W5ZEwGFlbCYe2DjT6HBtnMR3BMmLWEQVmpnG9uzB4s";

// Use service key for full access
const SUPABASE_SERVICE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJibWxlcW1mZWV3bWt2bWx4YmxmIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc2OTg4NTI2MSwiZXhwIjoyMDg1NDYxMjYxfQ.dp3MVDljKIZuhIW7m0NVotf_3IPkkZSHRJy444nMeVM";

const supabase = createClient(SUPABASE_URL, SUPABASE_SERVICE_KEY);

const TABLES_TO_CHECK = ["profiles", "users", "user_profiles", "mt5_accounts", "kyc_details", "transactions"];

const EXPECTED_PROFILE_FIELDS = ["id", "user_id", "first_name", "last_name", "email", "mobile", "country", "created_at", "updated_at"];

export default function SupabaseChecker() {
  const [results, setResults]     = useState({});
  const [loading, setLoading]     = useState(false);
  const [email, setEmail]         = useState("");
  const [userResult, setUserResult] = useState(null);
  const [activeTab, setActiveTab] = useState("tables");
  const [expandedTable, setExpandedTable] = useState(null);

  const checkAllTables = async () => {
    setLoading(true);
    setResults({});
    const out = {};

    for (const table of TABLES_TO_CHECK) {
      try {
        const { data, error, count } = await supabase
          .from(table)
          .select("*", { count: "exact" })
          .limit(5);

        if (error) {
          out[table] = { exists: false, error: error.message };
        } else {
          const columns = data && data.length > 0 ? Object.keys(data[0]) : [];
          const missingFields = table === "profiles" || table === "user_profiles"
            ? EXPECTED_PROFILE_FIELDS.filter(f => !columns.includes(f))
            : [];

          out[table] = {
            exists: true,
            count,
            columns,
            sample: data,
            missingFields,
          };
        }
      } catch (e) {
        out[table] = { exists: false, error: e.message };
      }
    }

    setResults(out);
    setLoading(false);
  };

  const checkUserByEmail = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setUserResult(null);

    try {
      // Check auth.users via admin (service key needed)
      const authCheck = await supabase.auth.admin.listUsers();
      const authUser = authCheck.data?.users?.find(u => u.email === email.trim());

      let profileData = null;
      let mt5Data = null;

      if (authUser) {
        // Check profiles table
        const { data: prof } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        // Also try user_id column
        if (!prof) {
          const { data: prof2 } = await supabase
            .from("profiles")
            .select("*")
            .eq("user_id", authUser.id)
            .single();
          profileData = prof2;
        } else {
          profileData = prof;
        }

        // Check mt5_accounts
        const { data: mt5 } = await supabase
          .from("mt5_accounts")
          .select("*")
          .eq("user_id", authUser.id);
        mt5Data = mt5;
      }

      setUserResult({ authUser, profileData, mt5Data });
    } catch (e) {
      setUserResult({ error: e.message });
    }

    setLoading(false);
  };

  const statusBadge = (ok, label) => (
    <span style={{
      padding: "2px 10px",
      borderRadius: 99,
      fontSize: 12,
      fontWeight: 600,
      background: ok ? "#dcfce7" : "#fee2e2",
      color: ok ? "#15803d" : "#dc2626",
    }}>{label}</span>
  );

  return (
    <div style={{ fontFamily: "Inter, sans-serif", background: "#f8fafc", minHeight: "100vh", padding: 24 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0 }}>
            🔍 Supabase DB Diagnostic
          </h1>
          <p style={{ color: "#64748b", marginTop: 4, fontSize: 14 }}>
            Check if registration data is saving correctly to your database
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {["tables", "user"].map(t => (
            <button key={t} onClick={() => setActiveTab(t)} style={{
              padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 13,
              background: activeTab === t ? "#16a34a" : "#e2e8f0",
              color: activeTab === t ? "#fff" : "#475569",
            }}>
              {t === "tables" ? "📋 All Tables" : "👤 Check by Email"}
            </button>
          ))}
        </div>

        {/* ── TAB: All Tables ── */}
        {activeTab === "tables" && (
          <>
            <button onClick={checkAllTables} disabled={loading} style={{
              background: "#16a34a", color: "#fff", border: "none",
              borderRadius: 8, padding: "10px 22px", fontWeight: 600,
              fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
              marginBottom: 20, opacity: loading ? 0.7 : 1,
            }}>
              {loading ? "⏳ Checking..." : "▶ Run Diagnostic"}
            </button>

            {Object.keys(results).length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {TABLES_TO_CHECK.map(table => {
                  const r = results[table];
                  const isOpen = expandedTable === table;
                  if (!r) return null;

                  return (
                    <div key={table} style={{
                      background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
                      overflow: "hidden",
                    }}>
                      <div
                        onClick={() => setExpandedTable(isOpen ? null : table)}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "14px 18px", cursor: "pointer",
                          background: isOpen ? "#f0fdf4" : "#fff",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
                            {table}
                          </span>
                          {r.exists
                            ? statusBadge(true, `✓ EXISTS  (${r.count ?? "?"} rows)`)
                            : statusBadge(false, "✗ NOT FOUND")}
                          {r.missingFields?.length > 0 &&
                            statusBadge(false, `⚠ ${r.missingFields.length} missing fields`)}
                        </div>
                        <span style={{ color: "#94a3b8", fontSize: 18 }}>{isOpen ? "▲" : "▼"}</span>
                      </div>

                      {isOpen && (
                        <div style={{ padding: "0 18px 18px" }}>
                          {!r.exists ? (
                            <p style={{ color: "#dc2626", fontSize: 13 }}>Error: {r.error}</p>
                          ) : (
                            <>
                              {/* Columns */}
                              <div style={{ marginBottom: 14 }}>
                                <p style={{ fontWeight: 600, fontSize: 13, color: "#475569", marginBottom: 6 }}>
                                  Columns ({r.columns.length}):
                                </p>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {r.columns.map(col => {
                                    const isMissing = r.missingFields?.includes(col);
                                    const isExpected = EXPECTED_PROFILE_FIELDS.includes(col);
                                    return (
                                      <span key={col} style={{
                                        padding: "2px 10px", borderRadius: 6, fontSize: 12,
                                        background: isMissing ? "#fee2e2" : isExpected ? "#dcfce7" : "#f1f5f9",
                                        color: isMissing ? "#dc2626" : isExpected ? "#15803d" : "#475569",
                                        fontFamily: "monospace",
                                      }}>{col}</span>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Missing fields warning */}
                              {r.missingFields?.length > 0 && (
                                <div style={{
                                  background: "#fef9c3", border: "1px solid #fde047",
                                  borderRadius: 8, padding: "10px 14px", marginBottom: 14,
                                }}>
                                  <p style={{ fontWeight: 700, fontSize: 13, color: "#854d0e", margin: "0 0 4px" }}>
                                    ⚠️ Missing Expected Fields:
                                  </p>
                                  <p style={{ fontSize: 13, color: "#713f12", margin: 0, fontFamily: "monospace" }}>
                                    {r.missingFields.join(", ")}
                                  </p>
                                </div>
                              )}

                              {/* Sample rows */}
                              {r.sample?.length > 0 ? (
                                <div>
                                  <p style={{ fontWeight: 600, fontSize: 13, color: "#475569", marginBottom: 6 }}>
                                    Sample rows (up to 5):
                                  </p>
                                  <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                      <thead>
                                        <tr style={{ background: "#f1f5f9" }}>
                                          {r.columns.map(col => (
                                            <th key={col} style={{
                                              padding: "6px 10px", textAlign: "left",
                                              borderBottom: "1px solid #e2e8f0", color: "#475569",
                                              fontFamily: "monospace", whiteSpace: "nowrap",
                                            }}>{col}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {r.sample.map((row, i) => (
                                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                                            {r.columns.map(col => (
                                              <td key={col} style={{
                                                padding: "6px 10px", borderBottom: "1px solid #f1f5f9",
                                                fontFamily: "monospace", maxWidth: 200,
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                color: row[col] === null || row[col] === "" ? "#dc2626" : "#0f172a",
                                              }}>
                                                {row[col] === null ? (
                                                  <em style={{ color: "#dc2626" }}>NULL</em>
                                                ) : row[col] === "" ? (
                                                  <em style={{ color: "#f59e0b" }}>empty</em>
                                                ) : String(row[col])}
                                              </td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : (
                                <p style={{ color: "#f59e0b", fontSize: 13, fontWeight: 600 }}>
                                  ⚠️ Table exists but has 0 rows — nothing is being saved!
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Check by Email ── */}
        {activeTab === "user" && (
          <div>
            <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
              <input
                type="email"
                placeholder="Enter user email (e.g. leadvala4@gmail.com)"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && checkUserByEmail()}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 8,
                  border: "1px solid #cbd5e1", fontSize: 14, outline: "none",
                }}
              />
              <button onClick={checkUserByEmail} disabled={loading} style={{
                background: "#16a34a", color: "#fff", border: "none",
                borderRadius: 8, padding: "10px 20px", fontWeight: 600,
                fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
              }}>
                {loading ? "⏳" : "Search"}
              </button>
            </div>

            {userResult && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                {userResult.error && (
                  <div style={{ background: "#fee2e2", borderRadius: 10, padding: 16 }}>
                    <p style={{ color: "#dc2626", fontWeight: 600 }}>Error: {userResult.error}</p>
                  </div>
                )}

                {/* Auth User */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>🔐 Auth User (auth.users)</span>
                    {userResult.authUser
                      ? statusBadge(true, "✓ Found")
                      : statusBadge(false, "✗ Not Found")}
                  </div>
                  {userResult.authUser ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      {[
                        ["User ID", userResult.authUser.id],
                        ["Email", userResult.authUser.email],
                        ["Email Confirmed", userResult.authUser.email_confirmed_at ? "✅ Yes" : "❌ No"],
                        ["Created At", userResult.authUser.created_at],
                        ["Last Sign In", userResult.authUser.last_sign_in_at || "Never"],
                        ["Full Name (metadata)", userResult.authUser.user_metadata?.full_name || "❌ EMPTY"],
                        ["Avatar URL", userResult.authUser.user_metadata?.avatar_url || "none"],
                      ].map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ padding: "5px 10px", color: "#64748b", fontWeight: 600, width: 200 }}>{k}</td>
                          <td style={{ padding: "5px 10px", fontFamily: "monospace", color: v?.toString().includes("❌") ? "#dc2626" : "#0f172a" }}>{v}</td>
                        </tr>
                      ))}
                    </table>
                  ) : (
                    <p style={{ color: "#dc2626", fontSize: 13 }}>No auth user found with this email.</p>
                  )}
                </div>

                {/* Profile */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>👤 Profile (profiles table)</span>
                    {userResult.profileData
                      ? statusBadge(true, "✓ Found")
                      : statusBadge(false, "✗ NOT SAVED — This is your bug!")}
                  </div>
                  {userResult.profileData ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      {Object.entries(userResult.profileData).map(([k, v]) => (
                        <tr key={k} style={{ background: (v === null || v === "") ? "#fef9c3" : "transparent" }}>
                          <td style={{ padding: "5px 10px", color: "#64748b", fontWeight: 600, width: 200 }}>{k}</td>
                          <td style={{ padding: "5px 10px", fontFamily: "monospace" }}>
                            {v === null ? <em style={{ color: "#dc2626" }}>NULL — not saved</em>
                              : v === "" ? <em style={{ color: "#f59e0b" }}>empty string</em>
                              : String(v)}
                          </td>
                        </tr>
                      ))}
                    </table>
                  ) : (
                    <div style={{ background: "#fef9c3", borderRadius: 8, padding: 14 }}>
                      <p style={{ fontWeight: 700, color: "#854d0e", margin: "0 0 6px" }}>
                        ⚠️ No profile row found for this user
                      </p>
                      <p style={{ color: "#713f12", fontSize: 13, margin: 0 }}>
                        Your registration flow is NOT inserting a row into the <code>profiles</code> table.
                        Check your sign-up handler — it likely needs to call{" "}
                        <code>supabase.from('profiles').insert([&#123; id: user.id, ... &#125;])</code>
                        after <code>supabase.auth.signUp()</code>.
                      </p>
                    </div>
                  )}
                </div>

                {/* MT5 Accounts */}
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>📊 MT5 Accounts</span>
                    {userResult.mt5Data?.length > 0
                      ? statusBadge(true, `✓ ${userResult.mt5Data.length} account(s)`)
                      : statusBadge(false, "✗ No accounts")}
                  </div>
                  {userResult.mt5Data?.length > 0 ? (
                    userResult.mt5Data.map((acc, i) => (
                      <div key={i} style={{ background: "#f8fafc", borderRadius: 8, padding: 12, marginBottom: 8, fontSize: 13, fontFamily: "monospace" }}>
                        {Object.entries(acc).map(([k, v]) => (
                          <div key={k} style={{ display: "flex", gap: 10 }}>
                            <span style={{ color: "#64748b", minWidth: 140 }}>{k}:</span>
                            <span style={{ color: v === null ? "#dc2626" : "#0f172a" }}>
                              {v === null ? "NULL" : String(v)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <p style={{ color: "#64748b", fontSize: 13 }}>No MT5 accounts found for this user.</p>
                  )}
                </div>

              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}